// Which header tabs the operator may use right now.
//
// The rule is "what is the machine doing", not "what has the operator touched": an exclusive
// operation (a dedicated inward run, a standalone purge) owns the operator for its duration,
// while a fulfillment or cycle count run leaves them the screens that run can legitimately
// interleave with — inward during fulfillment when the layout has dedicated inward gates, and
// decommission once the run has actually produced a tote to decommission.
//
// It lives here as a pure function because both consumers in App must agree: the disabled state
// on the links and the redirect off a tab that just became disabled. Deriving those separately is
// how you end up navigable-but-greyed (or greyed-but-stuck).

import {
  CYCLE_COUNTING_OPERATION_MODE,
  FULFILLMENT_OPERATION_MODE,
  GatingOperationMode,
  INWARD_OPERATION_MODE,
} from "./inwardOrchestration";

export type OperatorTab =
  | "orderFulfillment"
  | "materialInward"
  | "materialReplenish"
  | "decommission"
  | "cycleCount";

export interface TabGatingInput {
  /**
   * Both polls behind the gating — orchestrations and decommission tasks — have answered at
   * least once. They are independent, so until both have landed the app cannot tell "a purge is
   * the only thing running" from "fulfillment is running and its tasks simply haven't arrived
   * yet", and gating on the half-known state would bounce the operator to the wrong screen.
   */
  gatingReady: boolean;
  /** Operation mode of the active orchestration, if one is running. */
  activeOperationMode?: GatingOperationMode;
  /** The active fulfillment run can inward in flight (a station's original purpose is Inward). */
  isInflightInward: boolean;
  /** At least one decommission or purge-tote task is pending. */
  hasDecommissionWork: boolean;
}

export const TAB_PATHS: Record<OperatorTab, string> = {
  orderFulfillment: "/order-id",
  materialInward: "/material-inward",
  materialReplenish: "/material-topup",
  decommission: "/decommission",
  cycleCount: "/cycle-count",
};

// Every route the app serves, mapped back to the tab that owns it — a tab is only truly disabled
// if its sub-pages are unreachable too. Order matters: the fulfillment entry is last because "/"
// is its own landing route and everything unmatched falls through to it (App's catch-all renders
// Order Fulfillment).
const TAB_ROUTE_PREFIXES: ReadonlyArray<{ tab: OperatorTab; prefixes: readonly string[] }> = [
  { tab: "materialInward", prefixes: ["/material-inward", "/pickup_tote"] },
  { tab: "materialReplenish", prefixes: ["/material-topup", "/drop_details"] },
  { tab: "decommission", prefixes: ["/decommission"] },
  { tab: "cycleCount", prefixes: ["/cycle-count"] },
  {
    tab: "orderFulfillment",
    prefixes: ["/order-id", "/task-id", "/sku_bin", "/pickup_bin", "/order_details", "/"],
  },
];

// Every tab the operator may open when nothing is holding them, in landing-preference order.
// Material Replenish is absent by design — top-up/replenishment orchestration is not in use.
const BROWSABLE_TABS: readonly OperatorTab[] = [
  "orderFulfillment",
  "materialInward",
  "decommission",
  "cycleCount",
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  if (prefix === "/") {
    return pathname === "/";
  }

  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function resolveTabForPath(pathname: string): OperatorTab {
  const match = TAB_ROUTE_PREFIXES.find(({ prefixes }) =>
    prefixes.some((prefix) => matchesPrefix(pathname, prefix))
  );

  return match?.tab ?? "orderFulfillment";
}

/**
 * Tabs the operator may open, most relevant first — the head of the list is where they are sent
 * when the tab they are on gets disabled.
 *
 * Material Replenish is never returned: top-up/replenishment orchestration is not in use, so the
 * tab stays disabled in every state.
 */
export function resolveEnabledTabs({
  gatingReady,
  activeOperationMode,
  isInflightInward,
  hasDecommissionWork,
}: TabGatingInput): OperatorTab[] {
  // Restrict nothing until the state is actually known: a wrong redirect costs the operator
  // their place, a late one costs them a second.
  if (!gatingReady) {
    return [...BROWSABLE_TABS];
  }

  // A count of zero empties the bin, and with decommission-if-empty on the orchestrator then
  // decommissions it and waits for the operator to do so before the run can finish. Keeping the
  // tab shut here would deadlock the count: the task can only be done from Decommission.
  if (activeOperationMode === CYCLE_COUNTING_OPERATION_MODE) {
    const tabs: OperatorTab[] = ["cycleCount"];

    if (hasDecommissionWork) {
      tabs.push("decommission");
    }

    return tabs;
  }

  // A dedicated Inward run is inward and nothing else — unlike in-flight inward, the operator
  // chose it and ends it with Exit Inward.
  if (activeOperationMode === INWARD_OPERATION_MODE) {
    return ["materialInward"];
  }

  if (activeOperationMode === FULFILLMENT_OPERATION_MODE) {
    const tabs: OperatorTab[] = ["orderFulfillment"];

    if (isInflightInward) {
      tabs.push("materialInward");
    }

    if (hasDecommissionWork) {
      tabs.push("decommission");
    }

    return tabs;
  }

  // Nothing is orchestrating. Purging totes is then the operator's whole job for as long as the
  // tasks last; with no work at all every screen except replenishment stays browsable.
  if (hasDecommissionWork) {
    return ["decommission"];
  }

  return [...BROWSABLE_TABS];
}

/**
 * Where to send an operator whose current route just became disabled, or `null` to leave them
 * where they are. Returning `null` for "already there" keeps the caller's effect from looping.
 */
export function resolveRedirectPath(
  pathname: string,
  enabledTabs: readonly OperatorTab[]
): string | null {
  if (enabledTabs.length === 0 || enabledTabs.includes(resolveTabForPath(pathname))) {
    return null;
  }

  const target = TAB_PATHS[enabledTabs[0]];

  return target === pathname ? null : target;
}

/** Tooltip on a disabled tab, so the operator knows what is holding them rather than guessing. */
export function resolveDisabledTabTitle(
  tab: OperatorTab,
  { activeOperationMode, hasDecommissionWork }: TabGatingInput
): string {
  if (tab === "materialReplenish") {
    return "Material replenishment is not in use";
  }

  switch (activeOperationMode) {
    case CYCLE_COUNTING_OPERATION_MODE:
      return "Cycle count in progress";
    case INWARD_OPERATION_MODE:
      return "Inward in progress — finish or exit inward first";
    case FULFILLMENT_OPERATION_MODE:
      return "Not available during order fulfillment";
    default:
      return hasDecommissionWork
        ? "Finish the pending decommission tasks first"
        : "Not available right now";
  }
}
